import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import api, { money } from "@/lib/api";
import { openPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Loader2, Check, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

export default function Estimates() {
  const { isViewer } = useAuth();
  const [ests, setEsts] = useState(null);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/estimates").then(({ data }) => setEsts(data)).catch(() => setEsts([]));
  useEffect(() => { load(); }, []);

  const act = async (action) => {
    setBusy(true);
    try {
      await api.put(`/estimates/${active.id}/approval`, { action, note });
      toast.success(action === "approve" ? "Estimate approved" : action === "decline" ? "Estimate declined" : "Revision requested");
      setActive(null); setNote(""); load();
    } catch (e) { toast.error("Action failed"); }
    setBusy(false);
  };

  return (
    <DashboardLayout nav={getNav("client")} title="Estimates">
      {!ests ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        ests.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-slate-200"><FileText className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No estimates yet.</p></div> : (
          <div className="space-y-3">
            {ests.map((e) => (
              <button key={e.id} data-testid={`estimate-row-${e.id}`} onClick={() => setActive(e)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 transition-colors flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2"><span className="font-medium text-slate-900">{e.estimate_number}</span><StatusBadge status={e.status} /></div>
                  <div className="text-sm text-slate-500 mt-1">{e.work_order?.wo_number} · {e.property?.name}</div>
                </div>
                <div className="font-head text-xl font-semibold text-slate-900">{money(e.total)}</div>
              </button>
            ))}
          </div>
        )}

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setNote(""); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-head">Estimate {active.estimate_number}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="text-sm text-slate-500">{active.property?.name} · {active.work_order?.wo_number}</div>
                <div><div className="text-xs uppercase font-bold text-slate-400">Scope of Work</div><p className="mt-1 text-slate-700 text-sm">{active.scope_of_work}</p></div>
                {active.items?.length > 0 && (
                  <div className="border border-slate-200 rounded-lg divide-y">
                    {active.items.map((it, i) => (
                      <div key={i} className="flex justify-between p-3 text-sm"><span>{it.description} × {it.quantity}</span><span className="font-medium">{money(it.quantity * it.unit_price)}</span></div>
                    ))}
                  </div>
                )}
                <div className="space-y-1 text-sm">
                  {active.labor > 0 && <Row l="Labor" v={money(active.labor)} />}
                  {active.materials > 0 && <Row l="Materials" v={money(active.materials)} />}
                  {active.additional_fees > 0 && <Row l="Additional Fees" v={money(active.additional_fees)} />}
                  {active.tax > 0 && <Row l="Tax" v={money(active.tax)} />}
                  <div className="flex justify-between pt-2 border-t font-head font-semibold text-lg"><span>Total</span><span>{money(active.total)}</span></div>
                </div>
                {active.terms && <p className="text-xs text-slate-400">{active.terms}</p>}
                <Button data-testid="estimate-pdf" variant="outline" size="sm" className="w-full" onClick={() => openPdf(`/estimates/${active.id}/pdf`)}>Download PDF</Button>
                {active.status === "sent" && !isViewer ? (
                  <>
                    <Textarea placeholder="Optional note (for revision request)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="estimate-note" />
                    <div className="grid grid-cols-3 gap-2">
                      <Button data-testid="estimate-approve" disabled={busy} onClick={() => act("approve")} className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                      <Button data-testid="estimate-revise" disabled={busy} variant="outline" onClick={() => act("request_revision")}><RotateCcw className="w-4 h-4 mr-1" /> Revise</Button>
                      <Button data-testid="estimate-decline" disabled={busy} variant="outline" className="text-rose-600 border-rose-200" onClick={() => act("decline")}><X className="w-4 h-4 mr-1" /> Decline</Button>
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">Status: <StatusBadge status={active.status} /> {active.approved_at && `· Approved ${new Date(active.approved_at).toLocaleString()}`}</div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
const Row = ({ l, v }) => <div className="flex justify-between text-slate-600"><span>{l}</span><span>{v}</span></div>;
